import React from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '../../lib/utils';
import type { FilterCategoryItem } from './types';

interface FilterSidebarProps {
  categories: FilterCategoryItem[];
  activeCategory: string;
  onCategoryChange: (categoryId: string) => void;
  activeCounts: Record<string, number>;
  className?: string;
}

export const FilterSidebar: React.FC<FilterSidebarProps> = ({
  categories,
  activeCategory,
  onCategoryChange,
  activeCounts,
  className,
}) => {
  const { t } = useTranslation();
  return (
    <div
      className={cn(
        'w-56 flex-shrink-0 border-r border-border-subtle bg-surface-container-low',
        'flex flex-col overflow-hidden',
        className
      )}
    >
      {/* Header */}
      <div className="px-4 py-3 border-b border-border-subtle">
        <h3 className="text-xs font-semibold text-text-muted uppercase tracking-wider">
          {t('filters.categories')}
        </h3>
      </div>

      {/* Category list */}
      <nav className="flex-1 overflow-y-auto p-2">
        <ul className="space-y-1">
          {categories.map((category) => {
            const isActive = activeCategory === category.id;
            const activeCount = activeCounts[category.id] || 0;

            const Icon = category.icon;

            return (
              <li key={category.id}>
                <button
                  onClick={() => onCategoryChange(category.id)}
                  className={cn(
                    'w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left',
                    'transition-all focus:outline-none focus:ring-2 focus:ring-accent-primary',
                    isActive
                      ? 'bg-accent-primary/10 text-accent-primary'
                      : 'text-text-secondary hover:bg-surface-container hover:text-text-primary'
                  )}
                >
                  <Icon className={cn('w-4 h-4 flex-shrink-0', isActive && 'text-accent-primary')} />
                  <div className="flex-1 min-w-0">
                    <span className="text-sm font-medium block truncate">
                      {category.label}
                    </span>
                  </div>
                  {activeCount > 0 && (
                    <span
                      className={cn(
                        'flex-shrink-0 text-xs font-medium px-1.5 py-0.5 rounded-full min-w-[1.375rem] text-center tabular-nums',
                        isActive
                          ? 'bg-accent-primary text-black'
                          : 'bg-accent-primary/20 text-accent-primary'
                      )}
                    >
                      {activeCount}
                    </span>
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      </nav>
    </div>
  );
};

export default FilterSidebar;
